import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  Wallet
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import advancePaymentService from '../../services/advancePaymentService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const AdvancePayment = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalAdvances: 0,
    pendingAdvances: 0,
    approvedAdvances: 0,
    paidAdvances: 0,
    settledAdvances: 0,
    partiallySettledAdvances: 0,
    cancelledAdvances: 0,
    totalAdvanceAmount: 0,
    totalSettledAmount: 0,
    totalPendingAmount: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [advancesResponse, statsResponse] = await Promise.all([
        advancePaymentService.getAllAdvancePayments(),
        advancePaymentService.getAdvancePaymentStats()
      ])
      
      if (advancesResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (advancesResponse.data || []).map(advance => ({
          ...advance,
          id: advance._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (advancesResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No advance payment records found. Create your first advance payment!')
        }
      } else {
        toast.error(advancesResponse.message || 'Failed to load advance payments')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading advance payment records:', error)
      if (!error._errorHandled) {
        if (error.response?.status === 401) {
          toast.error('Authentication required. Please log in again.')
        } else if (error.response?.status === 403) {
          toast.error('You do not have permission to view advance payments')
        } else {
          toast.error('Failed to load advance payment records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'Pending': 'pending',
      'Approved': 'warning',
      'Paid': 'active',
      'Settled': 'active',
      'Partially Settled': 'warning',
      'Cancelled': 'inactive'
    }
    return statusMap[status] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'advanceNumber',
      header: 'Advance Number',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'employeeName',
      header: 'Employee',
      cell: ({ getValue, row }) => (
        <div className="service-description-cell" style={{ textAlign: 'left' }}>
          <span className="description-text">{getValue() || '-'}</span>
          {row.original.employeeCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.employeeCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'advanceDate',
      header: 'Advance Date',
      cell: ({ getValue }) => {
        const date = getValue()
        return (
          <div style={{ textAlign: 'center' }}>
            {date ? new Date(date).toLocaleDateString() : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'purpose',
      header: 'Purpose',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left', maxWidth: '200px' }}>
          <span style={{ 
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'advanceAmount',
      header: 'Advance Amount',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue(), row.original.currency || 'INR')}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const status = getValue()
        const badgeClass = getStatusBadgeClass(status)
        return (
          <div className="active-cell" style={{ textAlign: 'center' }}>
            <span className={`status-badge ${badgeClass}`}>
              {status || '-'}
            </span>
          </div>
        )
      },
    },
    {
      accessorKey: 'settledAmount',
      header: 'Settled',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'right', fontWeight: '500' }}>
          {formatCurrency(getValue() || 0, row.original.currency || 'INR')}
        </div>
      ),
    },
    {
      accessorKey: 'pendingAmount',
      header: 'Pending',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'right', fontWeight: '600', color: '#dc2626' }}>
          {formatCurrency(getValue() || 0, row.original.currency || 'INR')}
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/expenses/advance-payment/form')
  }

  const handleEdit = (advanceData) => {
    if (!['Pending', 'Approved'].includes(advanceData.status)) {
      toast.warning('Only pending or approved advances can be edited')
      return
    }
    navigate('/expenses/advance-payment/edit', { state: { advanceData } })
  }

  const handleView = (advanceData) => {
    navigate('/expenses/advance-payment/view', { state: { advanceData } })
  }

  const handleDelete = (advanceData) => {
    if (advanceData.status !== 'Pending') {
      toast.warning('Only pending advances can be deleted')
      return
    }
    setDeleteItem(advanceData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting advance payment...')
      const response = await advancePaymentService.deleteAdvancePayment(deleteItem._id)
      if (response.success) {
        toast.success('Advance payment deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete advance payment')
      }
    } catch (error) {
      console.error('Error deleting advance payment:', error)
      if (!error._errorHandled) {
        if (error.response?.status === 400) {
          toast.error(error.response.data?.message || 'Cannot delete this advance payment')
        } else if (error.response?.status === 404) {
          toast.error('Advance payment not found')
        } else {
          toast.error('Failed to delete advance payment. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    // TODO: Implement export functionality
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    {
      label: 'Expenses',
      path: '/expenses',
      onClick: () => navigate('/expenses')
    },
    {
      label: 'Advance Payments',
      path: '/expenses/advance-payment',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Advance Payments</h1>
          <p>Advance payments and settlement tracking</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Advance
          </button>
        </div>
      </div>

      {/* Metrics Section */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.totalAdvances}</span>
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalAdvances}</div>
          <div className="metric-title">Total Advances</div>
          <div className="metric-subtitle">All advance payments</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.pendingAdvances}</span>
            </div>
            <div className="metric-trend positive">
              <span>Pending</span>
            </div>
          </div>
          <div className="metric-value">{stats.pendingAdvances}</div>
          <div className="metric-title">Pending</div>
          <div className="metric-subtitle">Awaiting approval</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.paidAdvances}</span>
            </div>
            <div className="metric-trend positive">
              <span>Paid</span>
            </div>
          </div>
          <div className="metric-value">{stats.paidAdvances}</div>
          <div className="metric-title">Paid</div>
          <div className="metric-subtitle">Paid advances</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{formatCurrency(stats.totalPendingAmount)}</span>
            </div>
            <div className="metric-trend negative">
              <span>Pending</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(stats.totalPendingAmount)}</div>
          <div className="metric-title">Pending Amount</div>
          <div className="metric-subtitle">Amount pending settlement</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onExport={handleExport}
          noCard={true}
          className="sac-master-table"
          pageSize={10}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Advance Payment"
        message={`Are you sure you want to delete advance payment "${deleteItem?.advanceNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default AdvancePayment
